import {
  are_pins_compatible,
  calculate_connection_score,
  get_connection_confidence,
  get_preferred_connection_role,
} from './pinCompatibility';

const POWER_WIRE_COLOR = '#dc2626';   // red
const GROUND_WIRE_COLOR = '#111827';  // near-black

const SIGNAL_WIRE_COLORS = [
  '#2563eb', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
  '#6366f1', // indigo
];

const CONFIDENCE_FALLBACK_COLORS = {
  high: '#10b981',
  medium: '#f59e0b',
  low: '#ef4444',
  unknown: '#64748b',
};

const LED_FORWARD_VOLTAGE_BY_COLOR = {
  '#e60000': 2.0, // red
  '#16a34a': 2.2, // green
  '#2563eb': 3.0, // blue
  '#facc15': 2.1, // yellow
  '#f97316': 2.0, // orange
  '#e5e7eb': 3.0, // white
};

const DEFAULT_GPIO_VOLTAGE = 3.3;
const DEFAULT_SAFE_GPIO_CURRENT_MA = 8;
const DEFAULT_LED_MAX_CURRENT_MA = 20;

const get_led_forward_voltage = (led_pin) => {
  const color = String(
    led_pin?.led_color ||
    led_pin?.component_color ||
    ''
  ).trim().toLowerCase();

  return LED_FORWARD_VOLTAGE_BY_COLOR[color] || 2.0;
};

const get_resistor_value_ohms = (resistor_pin) => {
  const value = Number(
    resistor_pin?.resistance_ohms ||
    resistor_pin?.component_value_ohms ||
    resistor_pin?.valueOhms
  );

  return Number.isFinite(value) && value > 0 ? value : null;
};

const get_gpio_output_voltage = (gpio_pin) => {
  const voltage_text = String(
    gpio_pin?.voltage_range ||
    gpio_pin?.voltageRange ||
    ''
  ).toLowerCase();

  if (voltage_text.includes('5')) return 5;
  if (voltage_text.includes('3.3') || voltage_text.includes('3v3')) return 3.3;

  // Most modern MCU GPIOs are 3.3 V, and this is safer than assuming 5 V.
  return DEFAULT_GPIO_VOLTAGE;
};

const get_gpio_current_limit_ma = (gpio_pin) => {
  const current = Number.parseFloat(
    String(
      gpio_pin?.current_limit_ma ||
      gpio_pin?.currentLimitMa ||
      gpio_pin?.current_limit ||
      gpio_pin?.currentLimit ||
      ''
    )
  );

  if (Number.isFinite(current) && current > 0) {
    return current;
  }

  return DEFAULT_SAFE_GPIO_CURRENT_MA;
};

export const calculate_led_current_ma = ({ gpio_pin, resistor_pin, led_pin }) => {
  const resistor_ohms = get_resistor_value_ohms(resistor_pin);

  if (!resistor_ohms) {
    return null;
  }

  const gpio_voltage = get_gpio_output_voltage(gpio_pin);
  const led_forward_voltage = get_led_forward_voltage(led_pin);
  const voltage_across_resistor = gpio_voltage - led_forward_voltage;

  if (voltage_across_resistor <= 0) {
    return {
      current_ma: 0,
      resistor_ohms,
      gpio_voltage,
      led_forward_voltage,
      voltage_across_resistor,
    };
  }

  return {
    current_ma: (voltage_across_resistor / resistor_ohms) * 1000,
    resistor_ohms,
    gpio_voltage,
    led_forward_voltage,
    voltage_across_resistor,
  };
};

export const evaluate_led_current = ({ gpio_pin, resistor_pin, led_pin }) => {
  const result = calculate_led_current_ma({
    gpio_pin,
    resistor_pin,
    led_pin,
  });

  if (!result) {
    return {
      valid: true,
      confidence: 'medium',
      score: 60,
      label: 'Unknown LED current',
      reason: 'LED current could not be estimated because the resistor value is missing.',
    };
  }

  const gpio_limit_ma = get_gpio_current_limit_ma(gpio_pin);
  const led_max_current_ma = DEFAULT_LED_MAX_CURRENT_MA;
  const current = result.current_ma;

  if (current <= 0) {
    return {
      valid: true,
      confidence: 'low',
      score: 25,
      label: '~0 mA',
      reason: `LED may not light because the estimated GPIO voltage (${result.gpio_voltage} V) is not above the LED forward voltage (${result.led_forward_voltage} V).`,
      current_ma: current,
    };
  }

  if (current > led_max_current_ma || current > gpio_limit_ma) {
    return {
      valid: false,
      confidence: 'low',
      score: 0,
      label: `~${current.toFixed(1)} mA`,
      reason: `Estimated LED current is ~${current.toFixed(1)} mA, which may exceed the safe GPIO/LED current limit.`,
      current_ma: current,
    };
  }

  if (current >= 2 && current <= DEFAULT_SAFE_GPIO_CURRENT_MA) {
    return {
      valid: true,
      confidence: 'high',
      score: 95,
      label: `~${current.toFixed(1)} mA`,
      reason: `Estimated LED current is ~${current.toFixed(1)} mA, which is a safe beginner-friendly value.`,
      current_ma: current,
    };
  }

  if (current > DEFAULT_SAFE_GPIO_CURRENT_MA && current <= 15) {
    return {
      valid: true,
      confidence: 'medium',
      score: 70,
      label: `~${current.toFixed(1)} mA`,
      reason: `Estimated LED current is ~${current.toFixed(1)} mA. This should work, but a larger resistor would be gentler on the GPIO pin.`,
      current_ma: current,
    };
  }

  return {
    valid: true,
    confidence: 'medium',
    score: 55,
    label: `~${current.toFixed(1)} mA`,
    reason: `Estimated LED current is ~${current.toFixed(1)} mA. The LED may be dim but should be safe.`,
    current_ma: current,
  };
};

const is_board_pin = (pin) => {
  return pin?.component_type === 'board' && pin?.has_canvas_marker;
};

const strip_marker_index = (marker_key) => {
  return String(marker_key || '').replace(/__\d+$/, '');
};

const get_logical_connection_role = (pin) => {
  const role = get_preferred_connection_role(pin);

  if (role === 'gnd') return 'ground';
  if (role === 'vcc' || role === 'vin' || role === 'vdd' || role === '3v3' || role === '3.3v' || role === '5v') {
    return 'power';
  }

  return role || 'other';
};

const get_logical_existing_connection_keys = (connections, suggestion_pins) => {
  const pin_lookup = new Map();

  suggestion_pins.forEach((pin) => {
    pin_lookup.set(`${pin.component_id}:${pin.marker_key}`, pin);
  });

  const keys = new Set();

  (Array.isArray(connections) ? connections : []).forEach((connection) => {
    const from_pin = pin_lookup.get(`${connection.from.partId}:${connection.from.pinKey}`);
    const to_pin = pin_lookup.get(`${connection.to.partId}:${connection.to.pinKey}`);

    if (!from_pin || !to_pin) return;

    const from_role = get_logical_connection_role(from_pin);
    const to_role = get_logical_connection_role(to_pin);

    const pair_key = [
      from_pin.component_id,
      to_pin.component_id,
      from_role,
      to_role,
    ].join(':');

    const reverse_pair_key = [
      to_pin.component_id,
      from_pin.component_id,
      to_role,
      from_role,
    ].join(':');

    keys.add(pair_key);
    keys.add(reverse_pair_key);
  });

  return keys;
};

const build_logical_suggestion_key = (from_pin, to_pin) => {
  return [
    from_pin.component_id,
    to_pin.component_id,
    get_logical_connection_role(from_pin),
    get_logical_connection_role(to_pin),
  ].join(':');
};

const build_connection_key = (from, to) => {
  return [
    from?.component_id,
    from?.marker_key,
    to?.component_id,
    to?.marker_key,
  ].join(':');
};

const get_wire_color_role = (from_pin, to_pin) => {
  const from_role = get_logical_connection_role(from_pin);
  const to_role = get_logical_connection_role(to_pin);

  if (from_role === 'power' || to_role === 'power') {
    return 'power';
  }

  if (from_role === 'ground' || to_role === 'ground') {
    return 'ground';
  }

  return 'signal';
};

const get_suggested_wire_color = (from_pin, to_pin, signal_index = 0, confidence = 'unknown') => {
  const color_role = get_wire_color_role(from_pin, to_pin);

  if (color_role === 'power') {
    return POWER_WIRE_COLOR;
  }

  if (color_role === 'ground') {
    return GROUND_WIRE_COLOR;
  }

  if (color_role === 'signal') {
    return SIGNAL_WIRE_COLORS[signal_index % SIGNAL_WIRE_COLORS.length];
  }

  return CONFIDENCE_FALLBACK_COLORS[confidence] || '#2563eb';
};

const is_existing_connection = (connections, from, to) => {
  const forward_key = build_connection_key(
    {
      component_id: from.component_id,
      marker_key: from.marker_key,
    },
    {
      component_id: to.component_id,
      marker_key: to.marker_key,
    }
  );

  const reverse_key = build_connection_key(
    {
      component_id: to.component_id,
      marker_key: to.marker_key,
    },
    {
      component_id: from.component_id,
      marker_key: from.marker_key,
    }
  );

  return (Array.isArray(connections) ? connections : []).some((connection) => {
    const existing_forward_key = build_connection_key(
      {
        component_id: connection.from.partId,
        marker_key: connection.from.pinKey,
      },
      {
        component_id: connection.to.partId,
        marker_key: connection.to.pinKey,
      }
    );

    return existing_forward_key === forward_key || existing_forward_key === reverse_key;
  });
};

const get_pin_text_for_preference = (pin) => {
  return [
    pin?.pin_name,
    pin?.pinName,
    pin?.pin_number,
    pin?.pinNumber,
    pin?.pin_group_name,
    pin?.pinGroupName,
    pin?.group_name,
    pin?.groupName,
    pin?.header_name,
    pin?.headerName,
    pin?.description,
    pin?.functions,
    pin?.alternate_functions,
    pin?.alternateFunctions,
    pin?.pin_context_text,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

const get_pin_header_preference_score = (pin) => {
  const text = get_pin_text_for_preference(pin);
  const pin_name = String(pin?.pin_name || pin?.pinName || '').trim().toLowerCase();
  const pin_number = Number(pin?.pin_number || pin?.pinNumber || 0);
  const board_name = String(pin?.component_name || '').toLowerCase();
  const role = get_logical_connection_role(pin);

  let score = 0;

  // Prefer normal, user-facing supply labels.
  if (['5v', '3v3', '3.3v', 'vcc'].includes(pin_name)) {
    score += 45;
  }

  if (['gnd', 'ground'].includes(pin_name)) {
    score += 45;
  }

  // VIN is power, but usually not the best module VCC source.
  if (pin_name === 'vin') {
    score -= 30;
  }

  // Prefer explicitly documented user-facing headers if your API has these fields.
  if (/\bpower header\b/.test(text)) score += 160;
  else if (/\bpower\b/.test(text)) score += 80;

  if (/\bheader\b/.test(text)) score += 20;

  // Down-rank service/programming/debug headers.
  if (/\bicsp2?\b/.test(text)) score -= 260;
  if (/\bisp\b/.test(text)) score -= 180;
  if (/\bspi header\b/.test(text)) score -= 140;
  if (/\bprogramming\b/.test(text)) score -= 120;
  if (/\bdebug\b/.test(text)) score -= 90;
  if (/\bflash\b/.test(text)) score -= 90;

  // Arduino UNO-specific fallback based on your current pin ordering.
  // ICSP clusters: 7-12 and 13-18.
  // POWER header: 39-43.
  if (
    board_name.includes('arduino uno') &&
    ['power', 'ground'].includes(role)
  ) {
    if (pin_number >= 7 && pin_number <= 18) {
      score -= 260;
    }

    if (pin_number >= 39 && pin_number <= 43) {
      score += 180;
    }
  }

  return score;
};

const get_supply_duplicate_key = (pin) => {
  const role = get_logical_connection_role(pin);

  if (role !== 'power' && role !== 'ground') {
    return '';
  }

  const pin_name = String(pin?.pin_name || pin?.pinName || '')
    .trim()
    .toLowerCase();

  if (role === 'ground') {
    return `${pin.component_id}:ground`;
  }

  // Keep different supply rails separate:
  // 3V3, 5V, VIN, VCC should not collapse into one group.
  return `${pin.component_id}:power:${pin_name || 'power'}`;
};

const keep_preferred_duplicate_supply_pins = (pins) => {
  const best_supply_pin_by_key = new Map();
  const non_supply_pins = [];

  pins.forEach((pin) => {
    const duplicate_key = get_supply_duplicate_key(pin);

    if (!duplicate_key) {
      non_supply_pins.push(pin);
      return;
    }

    const current_best = best_supply_pin_by_key.get(duplicate_key);

    if (
      !current_best ||
      get_pin_header_preference_score(pin) > get_pin_header_preference_score(current_best)
    ) {
      best_supply_pin_by_key.set(duplicate_key, pin);
    }
  });

  return [
    ...non_supply_pins,
    ...Array.from(best_supply_pin_by_key.values()),
  ];
};

const get_pin_priority = (pin) => {
  const role = get_preferred_connection_role(pin);

  const priority = {
    power: 100,
    ground: 95,
    'i2c-sda': 90,
    'i2c-scl': 90,
    'spi-mosi': 80,
    'spi-miso': 80,
    'spi-sck': 80,
    'spi-cs': 75,
    'uart-tx': 70,
    'uart-rx': 70,
    analog: 60,
    pwm: 50,
    interrupt: 45,
    digital: 30,
  };

  return (priority[role] || 10) + get_pin_header_preference_score(pin);
};

export const generate_direct_board_suggestions = (suggestion_pins, existing_connections = []) => {
  const board_pins = keep_preferred_duplicate_supply_pins(
    (Array.isArray(suggestion_pins) ? suggestion_pins : [])
        .filter(is_board_pin)
  );

  console.log('Preferred board pins for suggestions:', board_pins
    .filter(pin => ['power', 'ground'].includes(get_logical_connection_role(pin)))
    .map(pin => ({
        component: pin.component_name,
        pinName: pin.pin_name,
        pinNumber: pin.pin_number,
        markerKey: pin.marker_key,
        role: get_logical_connection_role(pin),
        preference: get_pin_header_preference_score(pin),
        context: pin.pin_context_text,
    }))
  );

  const suggestions = [];
  const used_pin_ids = new Set();
  const used_logical_connection_keys = get_logical_existing_connection_keys(
    existing_connections,
    board_pins
  );

  let signal_color_index = 0;

  const sorted_pins = [...board_pins].sort((a, b) => {
    return get_pin_priority(b) - get_pin_priority(a);
  });

  for (const source_pin of sorted_pins) {
    if (used_pin_ids.has(String(source_pin.id))) {
      continue;
    }

    let best_match = null;
    let best_score = 0;

    for (const target_pin of sorted_pins) {
      if (source_pin.component_id === target_pin.component_id) {
        continue;
      }

      if (used_pin_ids.has(String(target_pin.id))) {
        continue;
      }

      if (is_existing_connection(existing_connections, source_pin, target_pin)) {
        continue;
      }

      const logical_key = build_logical_suggestion_key(source_pin, target_pin);
        const reverse_logical_key = build_logical_suggestion_key(target_pin, source_pin);

        if (
        used_logical_connection_keys.has(logical_key) ||
        used_logical_connection_keys.has(reverse_logical_key)
        ) {
        continue;
        }

      if (!are_pins_compatible(source_pin, target_pin)) {
        continue;
      }

      const score =
        calculate_connection_score(source_pin, target_pin) +
        get_pin_header_preference_score(source_pin) +
        get_pin_header_preference_score(target_pin);

      if (score < 50) {
        continue;
      }

      if (score > best_score) {
        best_score = score;
        best_match = target_pin;
      }
    }

    if (!best_match || best_score <= 0) {
      continue;
    }

    const confidence = get_connection_confidence(source_pin, best_match);

    const wire_color_role = get_wire_color_role(source_pin, best_match);
    const wire_color = get_suggested_wire_color(
    source_pin,
    best_match,
    signal_color_index,
    confidence
    );

    if (wire_color_role === 'signal') {
    signal_color_index += 1;
    }

    suggestions.push({
      id: `suggestion_${source_pin.component_id}_${source_pin.marker_key}_${best_match.component_id}_${best_match.marker_key}`,
      from: {
        partId: source_pin.component_id,
        boardName: source_pin.component_name,
        pinKey: source_pin.marker_key,
      },
      to: {
        partId: best_match.component_id,
        boardName: best_match.component_name,
        pinKey: best_match.marker_key,
      },
      color: wire_color,
      wireColorRole: wire_color_role,
      width: 2.4,
      confidence,
      score: best_score,
      suggested: true,
      reason: `${source_pin.display_name || source_pin.pin_name} matches ${best_match.display_name || best_match.pin_name}`,
    });

    used_pin_ids.add(String(source_pin.id));
    used_pin_ids.add(String(best_match.id));

    used_logical_connection_keys.add(build_logical_suggestion_key(source_pin, best_match));
    used_logical_connection_keys.add(build_logical_suggestion_key(best_match, source_pin));
  }

  return suggestions.sort((a, b) => b.score - a.score);
};

const is_passive_pin = (pin, passive_type) => {
  return (
    pin?.component_type === 'passive' &&
    pin?.passive_type === passive_type &&
    pin?.has_canvas_marker
  );
};

const is_gpio_output_pin = (pin) => {
  if (!is_board_pin(pin)) return false;

  const role = get_preferred_connection_role(pin);
  const pin_name = String(pin.pin_name || pin.pinName || '').trim().toLowerCase();

  // Avoid special/boot/flash/control pins for a beginner-friendly LED suggestion.
  const blocked_names = [
    'gnd',
    'ground',
    'vcc',
    'vin',
    'vdd',
    '3v3',
    '3.3v',
    '5v',
    'en',
    'rst',
    'reset',
    'tx',
    'rx',
    's0',
    's1',
    's2',
    's3',
    'sk',
    'sc',
  ];

  if (blocked_names.includes(pin_name)) {
    return false;
  }

  return ['pwm', 'digital', 'gpio'].includes(role);
};

const is_ground_pin = (pin) => {
  return get_logical_connection_role(pin) === 'ground';
};

const is_power_pin = (pin) => {
  return get_logical_connection_role(pin) === 'power';
};

const is_analog_input_pin = (pin) => {
  if (!is_board_pin(pin)) return false;

  const role = get_preferred_connection_role(pin);
  const pin_name = String(pin.pin_name || pin.pinName || '').trim().toLowerCase();
  const text = get_pin_text_for_preference(pin);

  return (
    role === 'analog' ||
    text.includes('analog') ||
    text.includes('adc') ||
    /^a\d+$/.test(pin_name)
  );
};

const is_buzzer_positive_pin = (pin) => {
  if (!is_passive_pin(pin, 'buzzer')) return false;

  const marker = strip_marker_index(pin.marker_key).toUpperCase();
  const role = get_preferred_connection_role(pin);
  const pin_name = String(pin.pin_name || pin.pinName || '').trim().toLowerCase();

  return (
    marker === '+' ||
    marker === 'A' ||
    marker === 'POS' ||
    marker === 'VCC' ||
    role === 'buzzer-positive' ||
    role === 'positive' ||
    pin_name.includes('+') ||
    pin_name.includes('positive') ||
    pin_name.includes('vcc')
  );
};

const is_buzzer_negative_pin = (pin) => {
  if (!is_passive_pin(pin, 'buzzer')) return false;

  const marker = strip_marker_index(pin.marker_key).toUpperCase();
  const role = get_preferred_connection_role(pin);
  const pin_name = String(pin.pin_name || pin.pinName || '').trim().toLowerCase();

  return (
    marker === '-' ||
    marker === 'K' ||
    marker === 'NEG' ||
    marker === 'GND' ||
    role === 'buzzer-negative' ||
    role === 'negative' ||
    pin_name.includes('-') ||
    pin_name.includes('negative') ||
    pin_name.includes('gnd')
  );
};

const get_power_pin_priority = (pin) => {
  const pin_name = String(pin.pin_name || pin.pinName || '').trim().toLowerCase();

  if (pin_name === '3v3' || pin_name === '3.3v') return 120;
  if (pin_name === '5v') return 100;
  if (pin_name === 'vcc' || pin_name === 'vdd') return 90;
  if (pin_name === 'vin') return 30;

  return 50 + get_pin_header_preference_score(pin);
};

const get_analog_pin_priority = (pin) => {
  const pin_name = String(pin.pin_name || pin.pinName || '').trim().toLowerCase();

  if (/^a0$/.test(pin_name)) return 100;
  if (/^a\d+$/.test(pin_name)) return 90;

  return 70 + get_pin_header_preference_score(pin);
};

const get_led_gpio_priority = (pin) => {
  const role = get_preferred_connection_role(pin);

  if (role === 'pwm') return 100;
  if (role === 'digital') return 80;
  if (role === 'gpio') return 70;

  return 0;
};

const build_endpoint_key = (endpoint) => {
  return [
    endpoint?.partId,
    strip_marker_index(endpoint?.pinKey),
  ].join(':');
};

const build_undirected_connection_key = (connection) => {
  const from_key = build_endpoint_key(connection.from);
  const to_key = build_endpoint_key(connection.to);

  return [from_key, to_key].sort().join('|');
};

const build_pin_lookup = (suggestion_pins) => {
  const pin_lookup = new Map();

  (Array.isArray(suggestion_pins) ? suggestion_pins : []).forEach((pin) => {
    const full_key = `${pin.component_id}:${pin.marker_key}`;
    const stripped_key = `${pin.component_id}:${strip_marker_index(pin.marker_key)}`;

    pin_lookup.set(full_key, pin);
    pin_lookup.set(stripped_key, pin);
  });

  return pin_lookup;
};

const get_connection_endpoint_pin = (pin_lookup, endpoint) => {
  return (
    pin_lookup.get(`${endpoint?.partId}:${endpoint?.pinKey}`) ||
    pin_lookup.get(`${endpoint?.partId}:${strip_marker_index(endpoint?.pinKey)}`)
  );
};

const build_undirected_logical_connection_key = (connection, pin_lookup) => {
  const from_pin = get_connection_endpoint_pin(pin_lookup, connection.from);
  const to_pin = get_connection_endpoint_pin(pin_lookup, connection.to);

  if (!from_pin || !to_pin) {
    return '';
  }

  const from_key = [
    from_pin.component_id,
    get_logical_connection_role(from_pin),
  ].join(':');

  const to_key = [
    to_pin.component_id,
    get_logical_connection_role(to_pin),
  ].join(':');

  return [from_key, to_key].sort().join('|');
};

const dedupe_project_connections = (connections, suggestion_pins) => {
  const pin_lookup = build_pin_lookup(suggestion_pins);
  const used_exact_keys = new Set();
  const used_logical_keys = new Set();
  const deduped_connections = [];

  (Array.isArray(connections) ? connections : []).forEach((connection) => {
    const exact_key = build_undirected_connection_key(connection);
    const logical_key = build_undirected_logical_connection_key(connection, pin_lookup);

    if (used_exact_keys.has(exact_key)) {
      return;
    }

    if (logical_key && used_logical_keys.has(logical_key)) {
      return;
    }

    used_exact_keys.add(exact_key);

    if (logical_key) {
      used_logical_keys.add(logical_key);
    }

    deduped_connections.push(connection);
  });

  return deduped_connections;
};

const has_connection_between = (connections, part_a, marker_a, part_b, marker_b) => {
  return (Array.isArray(connections) ? connections : []).some((connection) => {
    const forward =
      connection.from.partId === part_a &&
      connection.from.pinKey === marker_a &&
      connection.to.partId === part_b &&
      connection.to.pinKey === marker_b;

    const reverse =
      connection.from.partId === part_b &&
      connection.from.pinKey === marker_b &&
      connection.to.partId === part_a &&
      connection.to.pinKey === marker_a;

    return forward || reverse;
  });
};

const build_project_connection = ({
  id,
  from_pin,
  to_pin,
  color,
  wireColorRole,
  confidence = 'medium',
  score = 60,
  reason = '',
  ...extra
}) => {
  return {
    id,
    from: {
      partId: from_pin.component_id,
      boardName: from_pin.component_name,
      pinKey: from_pin.marker_key,
    },
    to: {
      partId: to_pin.component_id,
      boardName: to_pin.component_name,
      pinKey: to_pin.marker_key,
    },
    color,
    width: 2.4,
    confidence,
    score,
    suggested: true,
    wireColorRole,
    reason,
    ...extra,
  };
};

export const generate_led_resistor_suggestions = (suggestion_pins, existing_connections = []) => {
  const pins = Array.isArray(suggestion_pins) ? suggestion_pins : [];

 const led_anodes = pins.filter((pin) =>
   is_passive_pin(pin, 'led') &&
   (
     get_preferred_connection_role(pin) === 'led-anode' ||
     strip_marker_index(pin.marker_key) === 'A'
   )
 );

 const led_cathodes = pins.filter((pin) =>
   is_passive_pin(pin, 'led') &&
   (
     get_preferred_connection_role(pin) === 'led-cathode' ||
     strip_marker_index(pin.marker_key) === 'K'
   )
 );

  const resistor_a_pins = pins.filter((pin) =>
    is_passive_pin(pin, 'resistor') &&
    strip_marker_index(pin.marker_key) === 'A'
  );

  const resistor_b_pins = pins.filter((pin) =>
    is_passive_pin(pin, 'resistor') &&
    strip_marker_index(pin.marker_key) === 'B'
  );

  const gpio_pins = pins
    .filter(is_gpio_output_pin)
    .sort((a, b) => get_led_gpio_priority(b) - get_led_gpio_priority(a));

  const ground_pins = keep_preferred_duplicate_supply_pins(
    pins.filter((pin) =>
        is_board_pin(pin) &&
        is_ground_pin(pin)
    )
  );

  const suggestions = [];
  const used_led_component_ids = new Set();
  const used_resistor_component_ids = new Set();
  const used_gpio_pin_ids = new Set();

  for (const led_anode of led_anodes) {
    if (used_led_component_ids.has(led_anode.component_id)) {
      continue;
    }

    const led_cathode = led_cathodes.find((pin) =>
      pin.component_id === led_anode.component_id
    );

    if (!led_cathode) {
      continue;
    }

    const resistor_a = resistor_a_pins.find((pin) =>
      !used_resistor_component_ids.has(pin.component_id)
    );

    if (!resistor_a) {
      continue;
    }

    const resistor_b = resistor_b_pins.find((pin) =>
      pin.component_id === resistor_a.component_id
    );

    if (!resistor_b) {
      continue;
    }

    const gpio_pin = gpio_pins.find((pin) =>
      !used_gpio_pin_ids.has(String(pin.id)) &&
      pin.component_id !== led_anode.component_id &&
      pin.component_id !== resistor_a.component_id
    );

    if (!gpio_pin) {
      continue;
    }

    const ground_pin = [...ground_pins]
    .filter((pin) => pin.component_id === gpio_pin.component_id)
    .sort((a, b) => get_pin_header_preference_score(b) - get_pin_header_preference_score(a))[0]
    || [...ground_pins]
        .sort((a, b) => get_pin_header_preference_score(b) - get_pin_header_preference_score(a))[0];

    if (!ground_pin) {
      continue;
    }

    const led_current_evaluation = evaluate_led_current({
      gpio_pin,
      resistor_pin: resistor_a,
      led_pin: led_anode,
    });

    if (!led_current_evaluation.valid) {
      continue;
    }

    const candidate_connections = [
      build_project_connection({
        id: `suggestion_led_gpio_resistor_${gpio_pin.component_id}_${gpio_pin.marker_key}_${resistor_a.component_id}`,
        from_pin: gpio_pin,
        to_pin: resistor_a,
        color: '#2563eb',
        wireColorRole: 'signal',
        confidence: led_current_evaluation.confidence,
        score: led_current_evaluation.score,
        reason: `GPIO/PWM output drives the LED through a current-limiting resistor. ${led_current_evaluation.reason}`,
        electricalNote: led_current_evaluation.label,
        estimatedCurrentMa: led_current_evaluation.current_ma,
        pattern: 'led-resistor'
      }),

      build_project_connection({
        id: `suggestion_led_resistor_anode_${resistor_b.component_id}_${led_anode.component_id}`,
        from_pin: resistor_b,
        to_pin: led_anode,
        color: '#8b5cf6',
        wireColorRole: 'signal',
        confidence: led_current_evaluation.confidence,
        score: led_current_evaluation.score,
        reason: `Resistor output connects to LED anode. ${led_current_evaluation.reason}`,
        electricalNote: led_current_evaluation.label,
        estimatedCurrentMa: led_current_evaluation.current_ma,
        pattern: 'led-resistor'
      }),

      build_project_connection({
        id: `suggestion_led_cathode_ground_${led_cathode.component_id}_${ground_pin.component_id}_${ground_pin.marker_key}`,
        from_pin: led_cathode,
        to_pin: ground_pin,
        color: '#111827',
        wireColorRole: 'ground',
        confidence: 'high',
        score: 95,
        reason: 'LED cathode connects to ground.',
        pattern: 'led-resistor'
      }),
    ];

    const new_connections = candidate_connections.filter((connection) =>
      !has_connection_between(
        existing_connections,
        connection.from.partId,
        connection.from.pinKey,
        connection.to.partId,
        connection.to.pinKey
      )
    );

    if (new_connections.length === 0) {
      continue;
    }

    suggestions.push(...new_connections);

    used_led_component_ids.add(led_anode.component_id);
    used_resistor_component_ids.add(resistor_a.component_id);
    used_gpio_pin_ids.add(String(gpio_pin.id));
  }

  return suggestions;
};

export const generate_potentiometer_suggestions = (suggestion_pins, existing_connections = []) => {
  const pins = Array.isArray(suggestion_pins) ? suggestion_pins : [];

  const pot_a_pins = pins.filter((pin) =>
    is_passive_pin(pin, 'potentiometer') &&
    strip_marker_index(pin.marker_key) === 'A'
  );

  const pot_w_pins = pins.filter((pin) =>
    is_passive_pin(pin, 'potentiometer') &&
    strip_marker_index(pin.marker_key) === 'W'
  );

  const pot_b_pins = pins.filter((pin) =>
    is_passive_pin(pin, 'potentiometer') &&
    strip_marker_index(pin.marker_key) === 'B'
  );

  const power_pins = keep_preferred_duplicate_supply_pins(
    pins
      .filter((pin) => is_board_pin(pin) && is_power_pin(pin))
      .sort((a, b) => get_power_pin_priority(b) - get_power_pin_priority(a))
  );

  const ground_pins = keep_preferred_duplicate_supply_pins(
    pins
      .filter((pin) => is_board_pin(pin) && is_ground_pin(pin))
      .sort((a, b) => get_pin_header_preference_score(b) - get_pin_header_preference_score(a))
  );

  const analog_pins = pins
    .filter(is_analog_input_pin)
    .sort((a, b) => get_analog_pin_priority(b) - get_analog_pin_priority(a));

  const suggestions = [];
  const used_pot_component_ids = new Set();
  const used_analog_pin_ids = new Set();

  for (const pot_a of pot_a_pins) {
    if (used_pot_component_ids.has(pot_a.component_id)) {
      continue;
    }

    const pot_w = pot_w_pins.find((pin) => pin.component_id === pot_a.component_id);
    const pot_b = pot_b_pins.find((pin) => pin.component_id === pot_a.component_id);

    if (!pot_w || !pot_b) {
      continue;
    }

    const analog_pin = analog_pins.find((pin) =>
      !used_analog_pin_ids.has(String(pin.id)) &&
      pin.component_id !== pot_a.component_id
    );

    if (!analog_pin) {
      continue;
    }

    const power_pin =
      power_pins.find((pin) => pin.component_id === analog_pin.component_id) ||
      power_pins[0];

    const ground_pin =
      ground_pins.find((pin) => pin.component_id === analog_pin.component_id) ||
      ground_pins[0];

    if (!power_pin || !ground_pin) {
      continue;
    }

    const candidate_connections = [
      build_project_connection({
        id: `suggestion_pot_power_${power_pin.component_id}_${power_pin.marker_key}_${pot_a.component_id}`,
        from_pin: power_pin,
        to_pin: pot_a,
        color: POWER_WIRE_COLOR,
        wireColorRole: 'power',
        confidence: 'high',
        score: 95,
        reason: 'Potentiometer end terminal connects to the board voltage reference.',
        pattern: 'potentiometer',
      }),

      build_project_connection({
        id: `suggestion_pot_wiper_${pot_w.component_id}_${analog_pin.component_id}_${analog_pin.marker_key}`,
        from_pin: pot_w,
        to_pin: analog_pin,
        color: '#2563eb',
        wireColorRole: 'signal',
        confidence: 'high',
        score: 95,
        reason: 'Potentiometer wiper connects to an analog-capable input pin.',
        pattern: 'potentiometer',
      }),

      build_project_connection({
        id: `suggestion_pot_ground_${pot_b.component_id}_${ground_pin.component_id}_${ground_pin.marker_key}`,
        from_pin: pot_b,
        to_pin: ground_pin,
        color: GROUND_WIRE_COLOR,
        wireColorRole: 'ground',
        confidence: 'high',
        score: 95,
        reason: 'Potentiometer other end terminal connects to ground.',
        pattern: 'potentiometer',
      }),
    ];

    const new_connections = candidate_connections.filter((connection) =>
      !has_connection_between(
        existing_connections,
        connection.from.partId,
        connection.from.pinKey,
        connection.to.partId,
        connection.to.pinKey
      )
    );

    if (new_connections.length === 0) {
      continue;
    }

    suggestions.push(...new_connections);

    used_pot_component_ids.add(pot_a.component_id);
    used_analog_pin_ids.add(String(analog_pin.id));
  }

  return suggestions;
};

export const generate_buzzer_suggestions = (suggestion_pins, existing_connections = []) => {
  const pins = Array.isArray(suggestion_pins) ? suggestion_pins : [];

  const buzzer_positive_pins = pins.filter(is_buzzer_positive_pin);
  const buzzer_negative_pins = pins.filter(is_buzzer_negative_pin);

  const gpio_pins = pins
    .filter(is_gpio_output_pin)
    .sort((a, b) => get_led_gpio_priority(b) - get_led_gpio_priority(a));

  const ground_pins = keep_preferred_duplicate_supply_pins(
    pins.filter((pin) =>
      is_board_pin(pin) &&
      is_ground_pin(pin)
    )
  );

  const suggestions = [];
  const used_buzzer_component_ids = new Set();
  const used_gpio_pin_ids = new Set();

  for (const buzzer_positive of buzzer_positive_pins) {
    if (used_buzzer_component_ids.has(buzzer_positive.component_id)) {
      continue;
    }

    const buzzer_negative = buzzer_negative_pins.find((pin) =>
      pin.component_id === buzzer_positive.component_id
    );

    if (!buzzer_negative) {
      continue;
    }

    const gpio_pin = gpio_pins.find((pin) =>
      !used_gpio_pin_ids.has(String(pin.id)) &&
      pin.component_id !== buzzer_positive.component_id
    );

    if (!gpio_pin) {
      continue;
    }

    const ground_pin =
      ground_pins.find((pin) => pin.component_id === gpio_pin.component_id) ||
      ground_pins[0];

    if (!ground_pin) {
      continue;
    }

    const candidate_connections = [
      build_project_connection({
        id: `suggestion_buzzer_gpio_positive_${gpio_pin.component_id}_${gpio_pin.marker_key}_${buzzer_positive.component_id}`,
        from_pin: gpio_pin,
        to_pin: buzzer_positive,
        color: '#2563eb',
        wireColorRole: 'signal',
        confidence: 'medium',
        score: 80,
        reason: 'GPIO/PWM output drives the buzzer positive terminal.',
        pattern: 'buzzer',
      }),

      build_project_connection({
        id: `suggestion_buzzer_negative_ground_${buzzer_negative.component_id}_${ground_pin.component_id}_${ground_pin.marker_key}`,
        from_pin: buzzer_negative,
        to_pin: ground_pin,
        color: GROUND_WIRE_COLOR,
        wireColorRole: 'ground',
        confidence: 'high',
        score: 95,
        reason: 'Buzzer negative terminal connects to ground.',
        pattern: 'buzzer',
      }),
    ];

    const new_connections = candidate_connections.filter((connection) =>
      !has_connection_between(
        existing_connections,
        connection.from.partId,
        connection.from.pinKey,
        connection.to.partId,
        connection.to.pinKey
      )
    );

    if (new_connections.length === 0) {
      continue;
    }

    suggestions.push(...new_connections);

    used_buzzer_component_ids.add(buzzer_positive.component_id);
    used_gpio_pin_ids.add(String(gpio_pin.id));
  }

  return suggestions;
};

export const generate_project_wiring_suggestions = (suggestion_pins, existing_connections = []) => {
  const direct_suggestions = generate_direct_board_suggestions(
    suggestion_pins,
    existing_connections
  );

  const led_resistor_suggestions = generate_led_resistor_suggestions(
    suggestion_pins,
    [
      ...existing_connections,
      ...direct_suggestions,
    ]
  );

  const potentiometer_suggestions = generate_potentiometer_suggestions(
    suggestion_pins,
    [
      ...existing_connections,
      ...direct_suggestions,
      ...led_resistor_suggestions,
    ]
  );

  return dedupe_project_connections(
    [
      ...direct_suggestions,
      ...led_resistor_suggestions,
      ...potentiometer_suggestions,
    ],
    suggestion_pins
  );


};